import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IDeliveryOptionForm } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import SwitchActivation from '../../../components/layout/SwitchActivation';

const CreateDeliveryOptionForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['delivery-options', id],
    invalidateQueryKey: ['delivery-options'],
    id,
    singleGetApi: `/admin/delivery-options/${id}`,
    addApi: '/admin/delivery-options',
    editApi: `/admin/delivery-options/${id}`,
    itemName: t('delivery-option'),
    listRoute: '/delivery-options'
  });

  const validationSchema = Yup.object({
    name: Yup.object().shape({
      ar: Yup.string().required(t('required')),
      en: Yup.string().required(t('required'))
    }),
    description: Yup.object().shape({
      ar: Yup.string().required(t('required')),
      en: Yup.string().required(t('required'))
    }),
    is_active: Yup.number().required(t('required')).oneOf([0, 1], t('required'))
  });

  const initialValues: IDeliveryOptionForm = {
    name: { en: apiData?.name?.en || '', ar: apiData?.name?.ar || '' },
    description: { en: apiData?.description?.en || '', ar: apiData?.description?.ar || '' },
    is_active: apiData?.is_active !== undefined ? (apiData.is_active ? 1 : 0) : 0,
  };

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2, type: 'textarea' }
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = {
            ...values,
            _method: id ? 'PUT' : 'POST'
          };
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('delivery_option_details')} customStyle="last">
                <div className="inputs_group">
                  <FieldWrapper title={t('name_en')} inputName={`name[en]`} inputPlaceholder={t('name_en')} input customPadding />
                  <FieldWrapper title={t('name_ar')} inputName={`name[ar]`} inputPlaceholder={t('name_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('description_en')} inputName={`description[en]`} inputPlaceholder={t('description_en')} textArea customPadding />
                  <FieldWrapper title={t('description_ar')} inputName={`description[ar]`} inputPlaceholder={t('description_ar')} textArea customPadding />
                </div>
                <div className="switch_container">
                  <SwitchActivation onChange={(value: any) => formik.setFieldValue('is_active', value?.target?.checked ? 1 : 0)} active={formik.values.is_active} />
                  <p>
                    {t('active')} <span className="side_note">({t('active_means_enabled')})</span>
                  </p>
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateDeliveryOptionForm;
